// 6.13 The approval cards for WhatsApp: a reply the assistant drafted (approve_whatsapp_reply: approve, edit or
// reject, with the time left in the window) and a conversation it passed to you (whatsapp_handoff: what they said,
// the draft it would have sent, write your reply or give it back). Both act through lib/whatsapp_connect.ts.
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Crm } from "../../lib/crm";
import type { HumanTask } from "../../lib/types";
import { useAsync } from "../../lib/useAsync";
import { Async, Empty, Notice } from "../../components/ui";
import { DraftReply, HandOffButton, WaMessage, WindowChip, WriteWhatsApp } from "../pipeline/whatsappParts";

interface CardProps {
  crm: Crm;
  task: HumanTask;
  onDone: () => void;
}

/** approve_whatsapp_reply: one drafted reply, found in the inbox so the card always shows the latest text. */
export function WhatsAppReplyTask({ crm, task, onDone }: CardProps) {
  const outbox = String(task.payload.outbox_id ?? task.entity_id ?? "");
  const person = String(task.payload.person_id ?? "");
  const [msg, setMsg] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const data = useAsync(async () => {
    const inbox = await crm.whatsappInbox();
    const w = inbox.open.find((x) => x.drafts.some((d) => d.id === outbox)) ?? null;
    return { w, draft: w?.drafts.find((d) => d.id === outbox) ?? null };
  }, [crm, outbox]);
  return (
    <Async state={data} label="Loading the reply">
      {({ w, draft }) =>
        !w || !draft ? (
          <div>
            <Empty title="This reply can't go any more">
              <p>
                The 24-hour window has closed or the reply was decided elsewhere. Send the follow-up template from <Link to="/conversations/inbox?tab=whatsapp-closed">Connect › Conversations</Link>.
              </p>
            </Empty>
            <button type="button" className="btn btn-small" onClick={async () => { await crm.completeTask(task.id, "done", { handled: "window closed" }); onDone(); }}>
              Clear this card
            </button>
          </div>
        ) : (
          <div>
            <p className="card-lede">
              <strong>{w.name ?? "Someone"}</strong> wrote on WhatsApp. <WindowChip minutes={w.minutes_left} />
            </p>
            {w.last_in && <blockquote className="message-body pre">{w.last_in.body}</blockquote>}
            <Notice text={msg?.text ?? null} tone={msg?.tone} />
            <DraftReply
              crm={crm}
              draft={draft}
              open={w.minutes_left > 0}
              onDone={(text, tone) => {
                if (tone === "bad") setMsg({ text, tone });
                else onDone();
              }}
            />
            {person && (
              <p className="small">
                <Link to={`/contacts/people/${person}`}>Open their timeline</Link>
              </p>
            )}
          </div>
        )
      }
    </Async>
  );
}

/** whatsapp_handoff: the assistant wasn't sure, or they asked for a person. */
export function WhatsAppHandoffTask({ crm, task, onDone }: CardProps) {
  const person = String(task.payload.person_id ?? task.entity_id ?? "");
  const [msg, setMsg] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const thread = useAsync(() => crm.whatsappThread(person, 6), [crm, person]);
  const draft = typeof task.payload.draft === "string" ? task.payload.draft : "";
  const say = (text: string, tone: "ok" | "bad" = "ok") => {
    if (tone === "bad") setMsg({ text, tone });
    else onDone();
  };
  return (
    <Async state={thread} label="Loading the conversation">
      {(t) => (
        <div>
          <p className="card-lede">
            <strong>{String(task.payload.name ?? "Someone")}</strong> on WhatsApp: {String(task.payload.reason ?? "the assistant passed this to you")}.{" "}
            <WindowChip minutes={t.window?.minutes_left ?? 0} />
          </p>
          <ul className="wa-thread" aria-label="Their latest messages">
            {t.messages.slice(0, 4).map((m) => (
              <WaMessage key={m.id} m={m} />
            ))}
          </ul>
          {draft && (
            <p className="small">
              <strong>The assistant's draft:</strong> {draft}
            </p>
          )}
          <Notice text={msg?.text ?? null} tone={msg?.tone} />
          {t.window?.open ? (
            <WriteWhatsApp crm={crm} person={person} initial={draft} onDone={say} />
          ) : (
            <p className="small">The window has closed: send the follow-up template from Connect › WhatsApp, or reply from your phone if they write again.</p>
          )}
          <div className="inline wrap">
            <button
              type="button"
              className="btn btn-small"
              onClick={async () => {
                try {
                  await crm.handOffWhatsApp(person);
                  onDone();
                } catch (e) {
                  setMsg({ text: (e as Error).message, tone: "bad" });
                }
              }}
            >
              I'll reply from my phone
            </button>
            <HandOffButton crm={crm} person={person} handedOff onDone={say} />
          </div>
        </div>
      )}
    </Async>
  );
}
